import { useState, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { formatDistance, formatRelative } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import slugify from 'slugify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { z } from 'zod';

// src/constants/language.constant.ts
var LANGUAGES = [{ name: "English", code: "en-us", isDefault: true }];
var LANGUAGE_LABELS = LANGUAGES.reduce(
  (acc, lang) => {
    acc[lang.code] = lang.name;
    return acc;
  },
  {}
);
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay ?? 500);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
}

// src/utils/object.util.ts
function isEqual(x, y) {
  const ok = Object.keys;
  const tx = typeof x;
  const ty = typeof y;
  return x && y && tx === "object" && tx === ty ? ok(x).length === ok(y).length && ok(x).every((key) => isEqual(x[key], y[key])) : x === y;
}

// src/hooks/use-deep-compare-effect.ts
function useDeepCompareEffect(callback, dependencies) {
  const currentDependenciesRef = useRef();
  if (!isEqual(currentDependenciesRef.current, dependencies)) {
    currentDependenciesRef.current = dependencies;
  }
  useEffect(callback, [currentDependenciesRef.current]);
}
var useErrorState = create()(
  devtools(
    immer((set) => ({
      visible: false,
      icon: "",
      title: "",
      message: "",
      show: (params) => {
        set(
          (state) => {
            state.visible = true;
            state.icon = params.icon ?? null;
            state.title = params.title;
            state.message = params.message;
          },
          false,
          "error/show"
        );
      },
      hide: () => {
        set(
          (state) => {
            state.visible = false;
            state.title = "";
            state.message = "";
          },
          false,
          "error/hide"
        );
      }
    })),
    { enabled: process.env.NODE_ENV === "development" }
  )
);
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// src/utils/currency.util.ts
function toCurrency({
  price,
  locale = "vi-VN",
  currency = "VND",
  style = "decimal"
}) {
  const priceFormatted = new Intl.NumberFormat(
    locale,
    // BCP 47 language tag
    {
      style,
      currency
      // ISO 4217 currency code
    }
  ).format(price);
  return priceFormatted;
}
var WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
var WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(relativeTime);
function setDayJsLocale(locale) {
  dayjs.locale(locale);
}
function dateParse(date) {
  return dayjs(date, "DD/MM/YYYY").toDate();
}
function getDayName(date, weekday = "short") {
  const day = dateParse(date).getDay();
  return weekday === "long" ? WEEKDAYS_LONG[day] : WEEKDAYS_SHORT[day];
}
function getDaysBetweenTwoDates(from, to) {
  if (!from || !to) return 0;
  const dateFrom = dayjs(from, "DD/MM/YYYY");
  const dateTo = dayjs(to, "DD/MM/YYYY");
  return dateFrom.diff(dateTo, "day") + 1;
}
function getDayNameNative(date = /* @__PURE__ */ new Date(), locale = "en-us") {
  return date.toLocaleDateString(locale, { weekday: "short" });
}
function getFormatDistance(date, addSuffix = false, locale = "en-us") {
  let currentLocale = enUS;
  switch (locale) {
    case "vi-vn":
      currentLocale = vi;
      break;
    default:
      currentLocale = enUS;
  }
  return formatDistance(date, /* @__PURE__ */ new Date(), { addSuffix, locale: currentLocale });
}
function getFormatRelative(date, locale = "en-us") {
  let currentLocale = enUS;
  switch (locale) {
    case "vi-vn":
      currentLocale = vi;
      break;
    default:
      currentLocale = enUS;
  }
  return formatRelative(date, /* @__PURE__ */ new Date(), { locale: currentLocale });
}
function getFormatByLocale(locale = "en-us") {
  let formatString;
  switch (locale) {
    case "vi-vn":
      formatString = "dd/MM/yyyy";
      break;
    default:
      formatString = "MM/dd/yyyy";
  }
  return formatString;
}
function toDateTime(date, locale = "en-us", showTime = true) {
  let options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  };
  if (showTime) {
    options = {
      ...options,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    };
  }
  return date.toLocaleDateString(locale, options);
}
var setUTCStartOfDay = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const newDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  return newDate;
};
var setUTCEndOfDay = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const newDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  return newDate;
};
var getDateWithoutTimeZone = (date) => {
  if (!date) return /* @__PURE__ */ new Date();
  const dateString = date.toISOString().replace("Z", "");
  return new Date(dateString);
};
var convertToUTCISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
};

// src/utils/language.util.ts
var getLanguages = (locale) => {
  return LANGUAGES.sort((a, b) => a.code === locale ? -1 : b.code === locale ? 1 : 0);
};
function getShortName(name = "A") {
  const words = name.split(" ").slice(0, 2);
  const initials = words.map((word) => word[0]?.toUpperCase()).join("");
  return initials;
}
function convertBytes(byte) {
  const kiloByte = byte / 1024;
  const megaByte = kiloByte / 1024;
  const gigaByte = megaByte / 1024;
  if (gigaByte >= 1) {
    return `${gigaByte.toFixed(2)} GB`;
  } else if (megaByte >= 1) {
    return `${megaByte.toFixed(2)} MB`;
  } else if (kiloByte >= 1) {
    return `${kiloByte.toFixed(2)} KB`;
  } else {
    return `${byte} bytes`;
  }
}
var toSlug = (text) => {
  return slugify(text.toLowerCase(), {
    lower: true,
    remove: /[:;.,*+~!@#^&?(){}"'/[\]]/g
  });
};
function repeatStr(firstChar, char, length) {
  if (length === 0) return "";
  return firstChar + char.repeat(length);
}
function toCapitalized(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
var stripHTML = (htmlString) => {
  return htmlString.replace(/<\/?[^>]+(>|$)/g, "");
};
function objectToQueryString(obj) {
  return Object.keys(obj).filter((key) => {
    const value = obj[key];
    if (value === null || value === void 0 || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === "object" && Object.keys(value).length === 0) return false;
    return true;
  }).map((key) => {
    const value = obj[key];
    if (Array.isArray(value)) {
      return value.map((item, index) => `${encodeURIComponent(key)}[${index}]=${encodeURIComponent(item)}`).join("&");
    }
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }).join("&");
}
function isJson(text) {
  try {
    JSON.parse(text);
  } catch (e) {
    return false;
  }
  return true;
}

// src/utils/pagination.util.ts
function getPagingOffset(page = 1, limit = 10) {
  return (page - 1) * limit;
}
var baseValidator = {
  userName: stringSchema({
    min: 1,
    max: 50,
    required: true,
    minMessage: "validator_minimum_n_characters_allowed",
    maxMessage: "validator_maximum_n_characters_allowed",
    requiredMessage: "validator_user_name"
  }),
  email: stringSchema({
    min: 1,
    max: 320,
    minMessage: "validator_minimum_n_characters_allowed",
    maxMessage: "validator_maximum_n_characters_allowed",
    requiredMessage: "validator_email"
  }).email("validator_email_invalid"),
  password: passwordSchema({
    min: 8,
    max: 50
  }),
  confirmPassword: passwordSchema({
    min: 8,
    max: 50
  }),
  phoneNumber: phoneNumberSchema()
};
function stringSchema(options = {}) {
  const {
    min,
    max,
    required = false,
    minMessage = "validator_minimum_n_characters_allowed",
    maxMessage = "validator_maximum_n_characters_allowed",
    requiredMessage = "validator_required"
  } = options;
  let schema = z.string();
  if (required) {
    schema = schema.min(min ?? 1, requiredMessage);
  }
  if (min !== void 0) {
    schema = schema.min(min, minMessage);
  }
  if (max !== void 0) {
    schema = schema.max(max, maxMessage);
  }
  return schema;
}
function phoneNumberSchema(options = {}) {
  const { invalidMessage = "validator_phone_number_invalid" } = options;
  return z.string().refine(
    (value) => {
      try {
        const phoneNumber = parsePhoneNumberFromString(value);
        return phoneNumber?.isValid();
      } catch (error) {
        return false;
      }
    },
    {
      message: invalidMessage
    }
  );
}
function passwordSchema(options = {}) {
  const {
    min,
    max,
    required = false,
    pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/,
    minMessage = "validator_minimum_n_characters_allowed",
    maxMessage = "validator_maximum_n_characters_allowed",
    requiredMessage = "validator_required",
    patternMessage = "validator_password_rule"
  } = options;
  let schema = z.string();
  if (pattern !== void 0) {
    schema = schema.regex(pattern, patternMessage);
  }
  if (required) {
    schema = schema.min(min ?? 1, requiredMessage);
  }
  if (min !== void 0) {
    schema = schema.min(min, minMessage);
  }
  if (max !== void 0) {
    schema = schema.max(max, maxMessage);
  }
  return schema;
}
var createLocalizedField = (language) => ({
  min,
  max,
  required = false,
  minMessage = "validator_minimum_n_characters_allowed",
  maxMessage = "validator_maximum_n_characters_allowed",
  defaultRequiredMessage = "validator_default_language_required"
} = {}) => {
  const schema = z.array(z.object({ lang: z.string(), value: z.string() }));
  return schema.refine(
    (data) => {
      if (!required && (!data || data.length === 0)) return true;
      const defaultTranslationValue = data.find((item) => item.lang === language.code)?.value;
      return Boolean(defaultTranslationValue && stripHTML(defaultTranslationValue).trim().length > 0);
    },
    { message: defaultRequiredMessage }
  ).refine(
    (data) => {
      if (typeof min === "undefined") return true;
      return data.every((item) => stripHTML(item.value).trim().length >= min);
    },
    {
      message: minMessage
    }
  ).refine(
    (data) => {
      if (typeof max === "undefined") return true;
      return data.every((item) => stripHTML(item.value).trim().length <= max);
    },
    {
      message: maxMessage
    }
  );
};

export { LANGUAGES, LANGUAGE_LABELS, baseValidator, convertBytes, convertToUTCISO, createLocalizedField, dateParse, getDateWithoutTimeZone, getDayName, getDayNameNative, getDaysBetweenTwoDates, getFormatByLocale, getFormatDistance, getFormatRelative, getLanguages, getPagingOffset, getShortName, isEqual, isJson, objectToQueryString, passwordSchema, phoneNumberSchema, repeatStr, setDayJsLocale, setUTCEndOfDay, setUTCStartOfDay, stringSchema, stripHTML, toCapitalized, toCurrency, toDateTime, toSlug, useDebounce, useDeepCompareEffect, useErrorState, usePrevious };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map